// Apolo IDE — SynthesisView (Sprint v4 Epic S4 WS S4.3).
//
// Renders the Opus-synthesized output of a multi-agent dispatch with the
// attribution + confidence chips preserved. Consumes the `synthesis` payload
// returned by GET /api/agents/dispatch/:id which has shape:
//
//   { synthesized: string,
//     meta: { model, durationMs, tokensUsed, costUsd,
//             attributionCount, confidence: { high, medium, low, dominant },
//             fallback, fallbackReason, subagentCount } }
//
// Pure presentational. The hook (useSynthesis OR useOrchestrator) is in
// charge of fetching; this component just paints.
//
// Pure helpers live in `./synthesisHelpers.js` so the HMR boundary stays
// clean (Vite refuses non-component exports from .jsx files).

import { Sparkles, AlertTriangle } from 'lucide-react';
import {
  confidenceTone,
  decorateSynthText,
  fmtDuration,
  fmtCost,
} from './synthesisHelpers.js';

export default function SynthesisView({ synthesis, attribution, className = '' }) {
  if (!synthesis || typeof synthesis !== 'object') return null;
  const text = synthesis.synthesized || '';
  if (!text) return null;
  const meta = synthesis.meta || {};
  const isFallback = !!meta.fallback;
  const dominant = meta.confidence && meta.confidence.dominant;
  const decorated = decorateSynthText(text);
  const subagentCount = meta.subagentCount || (Array.isArray(attribution) ? attribution.length : 0);

  return (
    <section
      data-testid="synthesis-view"
      aria-label="Synthesized multi-agent response"
      className={`rounded-md border border-apolo-borderSubtle bg-apolo-surface1 p-3 text-[12px] ${className}`}
    >
      <header className="mb-2 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-apolo-accent/15 px-2 py-0.5 font-medium text-apolo-accent">
          <Sparkles size={11} aria-hidden="true" />
          Synthesized from {subagentCount} subagent{subagentCount === 1 ? '' : 's'}
        </span>
        <span data-testid="synthesis-duration" className="text-apolo-textTertiary">
          {fmtDuration(meta.durationMs)}
        </span>
        <span data-testid="synthesis-cost" className="text-apolo-textTertiary">
          {fmtCost(meta.costUsd)}
        </span>
        {dominant ? (
          <span
            data-testid="synthesis-confidence-chip"
            className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium ${confidenceTone(dominant)}`}
          >
            confidence: {dominant}
          </span>
        ) : null}
        {isFallback ? (
          <span
            data-testid="synthesis-fallback-badge"
            className="inline-flex items-center gap-1 rounded-full bg-apolo-warning/15 px-2 py-0.5 font-medium text-apolo-warning"
            title={meta.fallbackReason || 'Synthesis fallback'}
          >
            <AlertTriangle size={11} aria-hidden="true" />
            fallback
          </span>
        ) : null}
      </header>

      <article
        data-testid="synthesis-body"
        className="whitespace-pre-wrap leading-relaxed text-apolo-textPrimary"
      >
        {decorated.map((node) => {
          if (node.type === 'attribution') {
            return (
              <span
                key={node.key}
                data-testid={`synthesis-attr-${node.index}`}
                className="inline-block rounded bg-apolo-accent/10 px-1 font-mono text-[11px] text-apolo-accent"
                title={node.label}
              >
                [Subagent {node.index}: {node.label}]
              </span>
            );
          }
          if (node.type === 'confidence') {
            return (
              <span
                key={node.key}
                data-testid={`synthesis-conf-${node.level}`}
                className={`mx-0.5 inline-block rounded px-1 font-mono text-[10px] ${confidenceTone(node.level)}`}
              >
                {node.raw}
              </span>
            );
          }
          return <span key={node.key}>{node.value}</span>;
        })}
      </article>

      {Array.isArray(attribution) && attribution.length > 0 ? (
        <footer
          data-testid="synthesis-attribution-footer"
          className="mt-3 flex flex-wrap gap-1 border-t border-apolo-borderSubtle pt-2 text-[11px] text-apolo-textTertiary"
        >
          {attribution.map((a) => (
            <span
              key={`a-${a.index}`}
              data-testid={`synthesis-attr-source-${a.index}`}
              className="inline-flex items-center gap-1 rounded border border-apolo-borderSubtle px-1.5 py-0.5"
              title={`${a.goal} (${a.model})`}
            >
              <span className="font-mono text-apolo-accent">#{a.index}</span>
              <span className="max-w-[180px] truncate">{a.goal}</span>
              {typeof a.citationCount === 'number' && a.citationCount > 0 ? (
                <span className="font-mono">×{a.citationCount}</span>
              ) : null}
            </span>
          ))}
        </footer>
      ) : null}
    </section>
  );
}
